import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { Page, PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Wrench, Plus, Trash } from "@phosphor-icons/react";

export default function SoysosWorkers() {
  const qc = useQueryClient();
  const { data: workers = [] } = useQuery({ queryKey: ["sos-workers"], queryFn: async () => (await api.get("/soysos/workers")).data });
  const { data: config } = useQuery({ queryKey: ["sos-config"], queryFn: async () => (await api.get("/soysos/config")).data });
  const depts = config?.departments || [];

  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/soysos/workers/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["sos-workers"] }); qc.invalidateQueries({ queryKey: ["sos-config"] }); toast.success("Worker removed"); },
  });

  return (
    <Page>
      <PageHeader title="Workers" subtitle="Field staff (housekeepers, engineers, valets) you can assign requests to — no login required.">
        <AddWorkerDialog depts={depts} onDone={() => { qc.invalidateQueries({ queryKey: ["sos-workers"] }); qc.invalidateQueries({ queryKey: ["sos-config"] }); }} />
      </PageHeader>
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <table className="w-full text-sm" data-testid="workers-table">
          <thead className="bg-secondary/50 text-muted-foreground text-[12px] uppercase tracking-wide">
            <tr><th className="text-left font-semibold px-4 py-3">Name</th><th className="text-left font-semibold px-4 py-3">Department</th><th className="text-left font-semibold px-4 py-3">Phone</th><th className="text-left font-semibold px-4 py-3">Status</th><th className="px-4 py-3"></th></tr>
          </thead>
          <tbody>
            {workers.filter((w) => w.active).map((w) => (
              <tr key={w.id} className="border-t border-border" data-testid={`worker-row-${w.id}`}>
                <td className="px-4 py-3 font-medium flex items-center gap-2"><Wrench size={15} className="text-muted-foreground" /> {w.name}</td>
                <td className="px-4 py-3 capitalize">{w.department || "—"}</td>
                <td className="px-4 py-3 text-muted-foreground">{w.phone || "—"}</td>
                <td className="px-4 py-3"><span className="text-[11px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-200">active</span></td>
                <td className="px-4 py-3 text-right"><Button size="sm" variant="ghost" className="text-destructive" data-testid={`worker-remove-${w.id}`} onClick={() => remove.mutate(w.id)}><Trash size={15} /></Button></td>
              </tr>
            ))}
            {!workers.filter((w) => w.active).length && <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">No workers yet. Add your first field-staff member.</td></tr>}
          </tbody>
        </table>
      </div>
    </Page>
  );
}

function AddWorkerDialog({ depts, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", department: "", phone: "" });
  const add = useMutation({
    mutationFn: async () => (await api.post("/soysos/workers", f)).data,
    onSuccess: () => { toast.success("Worker added"); setOpen(false); setF({ name: "", department: "", phone: "" }); onDone?.(); },
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="add-worker-btn" className="gap-1.5"><Plus size={16} weight="bold" /> Add worker</Button></DialogTrigger>
      <DialogContent data-testid="add-worker-dialog">
        <DialogHeader><DialogTitle>Add a worker</DialogTitle><DialogDescription>Field staff you can assign requests to. They don't log in.</DialogDescription></DialogHeader>
        <div className="space-y-3 py-1">
          <div><Label className="text-xs">Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="worker-name" className="mt-1" placeholder="Rosa Martinez" /></div>
          <div><Label className="text-xs">Department</Label>
            <Select value={f.department} onValueChange={(v) => setF({ ...f, department: v })}>
              <SelectTrigger className="mt-1" data-testid="worker-dept"><SelectValue placeholder="Choose department" /></SelectTrigger>
              <SelectContent>{depts.map((d) => <SelectItem key={d.key} value={d.key}>{d.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Phone (optional)</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="worker-phone" className="mt-1" placeholder="+1 321 555 0100" /></div>
        </div>
        <DialogFooter><Button onClick={() => add.mutate()} disabled={!f.name || add.isPending} data-testid="worker-save">{add.isPending ? "Saving…" : "Add worker"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
